import React, { useState, useRef, useEffect } from 'react';
import SearchBox from '../search/SearchBox';
import IconButton from '../buttons/IconButton';
import NotificationPanel from '../notifications/NotificationPanel';
import { FaUserCircle } from "react-icons/fa";
import { FaBell } from "react-icons/fa";
import { RiMenuLine } from "react-icons/ri";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as LogoMark } from '../../images/logo.svg';
import { isActingAsAdmin, signOut } from '../../utils/authUtils';
import { useSearchDrawer } from '../../contexts/SearchDrawerContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useMobileNav } from '../../contexts/MobileNavContext';

import css from './Header.module.scss';

const Header: React.FC = () => {
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { openDrawer, atSearchLimit } = useSearchDrawer();
  const { unreadCount, clearUnread } = useNotifications();
  const { isNavOpen, toggleNav } = useMobileNav();

  const { pathname } = useLocation();

  const userFirstName = localStorage.getItem('userFirstName') || 'User';
  // WHERE, as well as who — the same correction the sidebar carries. `AdminShell`
  // renders this header too, so `isActingAsAdmin()` alone hid the app's controls
  // on the console for a plain admin and showed them for an IMPERSONATING one:
  // "Create New Job Search" sat on top of the admin Overview page, opening the
  // impersonated user's search drawer from a console surface.
  const hideButtons = isActingAsAdmin() || pathname.startsWith('/admin');

  const handleCreateNewSearchClick = () => {
    openDrawer();
  };

  const handleAccountClick = () => {
    setIsAccountMenuOpen(!isAccountMenuOpen);
  };

  const handleNotificationsClick = () => {
    setIsNotificationPanelOpen(!isNotificationPanelOpen);
    if (!isNotificationPanelOpen) {
      clearUnread();
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setIsAccountMenuOpen(false);
  };

  const handleSignOutClick = () => {
    setIsAccountMenuOpen(false);
    signOut(navigate);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationPanelOpen(false);
      }
    };

    // iOS doesn't reliably synthesize mousedown for taps on non-interactive
    // elements, so listen for touchstart as well.
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <div className={css.header}>
      <button
        className={css.mobileNavToggle}
        onClick={toggleNav}
        aria-label="Open menu"
        aria-expanded={isNavOpen}
      >
        <RiMenuLine />
      </button>
      <Link to="/" className={css.mobileLogo} aria-label="Job Beast home">
        <LogoMark />
      </Link>
      <div className={css.search}>
        {!hideButtons && (
          <button
            className={css.search}
            onClick={handleCreateNewSearchClick}
            disabled={atSearchLimit}
            title={
              atSearchLimit
                ? "You're at your search limit — pause or delete a search to add more."
                : undefined
            }
          >
            {atSearchLimit ? (
              'Search limit reached'
            ) : (
              <>
                <span className={css.createLabelFull}>Create New Job Search</span>
                <span className={css.createLabelShort}>New Search</span>
              </>
            )}
          </button>
        )}
      </div>
      <div className={css.controls}>
        <SearchBox />
        <div className={css.notificationMenu} ref={notificationRef}>
          <IconButton
            icon={FaBell}
            onClick={handleNotificationsClick}
            notificationCount={unreadCount}
            hoverColor='#207551'
          />
          {isNotificationPanelOpen && (
            <NotificationPanel onNavigate={() => setIsNotificationPanelOpen(false)} />
          )}
        </div>
        <div className={css.accountMenu} ref={accountMenuRef}>
          <IconButton
            icon={FaUserCircle}
            onClick={handleAccountClick}
            hoverColor='#207551'
            title={userFirstName}
          />
          {isAccountMenuOpen && (
            <div className={css.accountMenuContent}>
              <div className={css.accountMenuHeader}>{userFirstName}</div>
              <div className={css.accountMenuItem} onClick={handleSettingsClick}>Settings</div>
              <div className={css.accountMenuItem} onClick={handleSignOutClick}>Sign Out</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;